import React from 'react';

import { isEmpty } from 'lodash';

import Button from '~/core/atoms/Buttons/Button';
import IconButton from '~/core/atoms/Buttons/IconButton';
import Tags from '~/core/atoms/Tag/Tags';
import { TagData } from '~/core/atoms/Tag/types';
import TextArea from '~/core/atoms/TextArea';
import SendMessageIcon from '~/core/iconComponents/icons/SendMessageIcon';
import cn from '~/utils/cn.ts';

export interface ChatInputAction {
  key: string;
  icon: React.ReactNode;
  onClick: () => void;
  ariaLabel?: string;
  disabled?: boolean;
}

interface ChatInputProps {
  value: string;
  rows: number;
  disabled?: boolean;
  disableSubmit?: boolean;
  placeholder?: string;
  onTextChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onSubmit: (text: string) => void;
  className?: string;
  actions?: ChatInputAction[];
  tags?: TagData[];
  onTagClose?: (tagId: string | number) => void;
}

const ChatInput: React.FC<ChatInputProps> = ({
  value,
  rows,
  disabled = false,
  disableSubmit = false,
  placeholder,
  onTextChange,
  onSubmit,
  className,
  actions = [],
  tags = [],
  onTagClose = () => null,
}) => {
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!isEmpty(value)) {
        onSubmit(value);
      }
    }
  };

  return (
    <div className={cn('relative rounded-lg bg-white shadow-sm', className)}>
      {!isEmpty(tags) ? (
        <Tags tags={tags} shouldDisplayClose onTagClose={onTagClose} className="bg-transparent px-20 pt-20" />
      ) : null}

      <TextArea
        rows={rows}
        resizable={false}
        value={value}
        onChange={onTextChange}
        className="semibold rounded-xl bg-transparent py-[22px] pr-80 pl-24 text-sm focus:outline-none"
        placeholder={placeholder}
        disabled={disabled}
        onKeyDown={onKeyDown}
        autoResize
        maxHeight={300}
      />
      <div className="absolute right-16 bottom-12 flex items-center justify-center gap-12 rounded-full">
        {!isEmpty(actions) && (
          <div className="flex flex-row-reverse items-center gap-4">
            {actions.map((action) => (
              <IconButton
                key={action.key}
                onClick={action.onClick}
                aria-label={action.ariaLabel || 'action'}
                disabled={action.disabled}
                size="sm"
                className="flex items-center justify-center rounded-full p-0 hover:bg-slate-400"
              >
                {action.icon}
              </IconButton>
            ))}
          </div>
        )}
        <Button
          disabled={disabled || disableSubmit}
          onClick={() => onSubmit(value)}
          className="flex h-40 w-40 items-center justify-center rounded-full bg-pink-600 p-8 hover:bg-pink-700 disabled:bg-pink-500 disabled:hover:bg-pink-600/50"
        >
          <SendMessageIcon className="h-40 w-40 p-2" iconColor="white" />
        </Button>
      </div>
    </div>
  );
};

export default ChatInput;
